import { useState, useEffect, useRef } from 'react';
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Play, Pause } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function FullscreenView({ project, imageIndex, onClose, onIndexChange }) {
  const [currentIndex, setCurrentIndex] = useState(imageIndex);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPlaying, setIsPlaying] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [loadingImage, setLoadingImage] = useState(true);
  const intervalRef = useRef(null);
  const idleTimerRef = useRef(null);
  const touchStartRef = useRef(null);

  const images = project.images || [];

  useEffect(() => {
    setCurrentIndex(imageIndex);
  }, [imageIndex]);

  // Preload adjacent images
  useEffect(() => {
    if (images.length > 0) {
      const preloadIndices = [
        (currentIndex + 1) % images.length,
        (currentIndex - 1 + images.length) % images.length,
        (currentIndex + 2) % images.length
      ];

      preloadIndices.forEach(idx => {
        const img = new Image();
        img.src = images[idx].full_url;
      });
    }
  }, [currentIndex, images]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
      if (e.key === ' ') {
        e.preventDefault();
        setIsPlaying(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentIndex]);

  // Auto slideshow on idle
  useEffect(() => {
    const startIdleTimer = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => {
        if (images.length > 1) setIsPlaying(true);
      }, 5000);
    };

    const resetIdleTimer = () => {
      startIdleTimer();
      setIsPlaying(false);
    };

    window.addEventListener('mousemove', resetIdleTimer);
    window.addEventListener('touchstart', resetIdleTimer);
    startIdleTimer();

    return () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      window.removeEventListener('mousemove', resetIdleTimer);
      window.removeEventListener('touchstart', resetIdleTimer);
    };
  }, [images.length]);

  // Slideshow
  useEffect(() => {
    if (isPlaying && images.length > 1) {
      intervalRef.current = setInterval(() => {
        setCurrentIndex((prev) => {
          const next = (prev + 1) % images.length;
          onIndexChange(next);
          return next;
        });
      }, 3000);
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current);
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isPlaying, images.length]);

  const goToNext = () => {
    if (images.length === 0) return;
    const newIndex = (currentIndex + 1) % images.length;
    setCurrentIndex(newIndex);
    onIndexChange(newIndex);
    resetZoom();
    setLoadingImage(true);
  };

  const goToPrevious = () => {
    if (images.length === 0) return;
    const newIndex = (currentIndex - 1 + images.length) % images.length;
    setCurrentIndex(newIndex);
    onIndexChange(newIndex);
    resetZoom();
    setLoadingImage(true);
  };

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 0.5, 3));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 0.5, 1));
    if (zoom <= 1.5) resetZoom();
  };

  const resetZoom = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  const handleMouseDown = (e) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      const maxPan = 200;
      const newX = Math.max(-maxPan, Math.min(maxPan, e.clientX - dragStart.x));
      const newY = Math.max(-maxPan, Math.min(maxPan, e.clientY - dragStart.y));
      setPan({ x: newX, y: newY });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleDoubleClick = () => {
    if (zoom > 1) {
      resetZoom();
    } else {
      setZoom(2);
    }
  };

  // Touch gestures
  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(touch2.clientX - touch1.clientX, touch2.clientY - touch1.clientY);
      touchStartRef.current = { distance, zoom };
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 2 && touchStartRef.current) {
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(touch2.clientX - touch1.clientX, touch2.clientY - touch1.clientY);
      const scale = distance / touchStartRef.current.distance;
      const newZoom = Math.max(1, Math.min(3, touchStartRef.current.zoom * scale));
      setZoom(newZoom);
    }
  };

  if (images.length === 0) {
    return (
      <div className="fixed inset-0 z-50 bg-black flex items-center justify-center" data-testid="fullscreen-view">
        <Button onClick={onClose} className="absolute top-4 right-4" variant="ghost">
          <X size={24} className="text-white" />
        </Button>
        <p className="text-white">No images available</p>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black scale-in"
      data-testid="fullscreen-view"
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
    >
      {/* Top Controls */}
      <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between p-6 bg-gradient-to-b from-black/70 to-transparent">
        <div className="text-white max-w-2xl">
          <h2 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            {project.title}
          </h2>
          <p className="text-sm opacity-75 mt-1">{project.category}</p>
          {project.description && (
            <p className="text-sm opacity-90 mt-2 line-clamp-2">{project.description}</p>
          )}
        </div>
        <Button
          onClick={onClose}
          variant="ghost"
          className="text-white hover:bg-white/20"
          data-testid="close-fullscreen-btn"
        >
          <X size={24} />
        </Button>
      </div>

      {/* Image Display */}
      <div
        className="absolute inset-0 flex items-center justify-center"
        onMouseDown={handleMouseDown}
        onDoubleClick={handleDoubleClick}
        style={{ cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default' }}
      >
        {loadingImage && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-12 h-12 border-4 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#b8d71b' }} />
          </div>
        )}
        <img
          src={images[currentIndex].full_url}
          alt={project.title}
          className="max-w-full max-h-full object-contain transition-transform duration-300"
          style={{
            transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
            transition: isDragging ? 'none' : 'transform 0.3s ease'
          }}
          draggable={false}
          onLoad={() => setLoadingImage(false)}
        />
      </div>

      {/* Navigation */}
      {images.length > 1 && (
        <>
          <Button
            onClick={goToPrevious}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 text-white backdrop-blur-sm"
            data-testid="prev-image-btn"
          >
            <ChevronLeft size={32} />
          </Button>
          <Button
            onClick={goToNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 text-white backdrop-blur-sm"
            data-testid="next-image-btn"
          >
            <ChevronRight size={32} />
          </Button>
        </>
      )}

      {/* Bottom Controls */}
      <div className="absolute bottom-0 left-0 right-0 z-10 p-6 bg-gradient-to-t from-black/70 to-transparent">
        <div className="flex items-center justify-between max-w-4xl mx-auto">
          <div className="flex items-center gap-3">
            <Button
              onClick={handleZoomOut}
              variant="ghost"
              className="text-white hover:bg-white/20"
              disabled={zoom <= 1}
              data-testid="zoom-out-btn"
            >
              <ZoomOut size={20} />
            </Button>
            <span className="text-white text-sm">{Math.round(zoom * 100)}%</span>
            <Button
              onClick={handleZoomIn}
              variant="ghost"
              className="text-white hover:bg-white/20"
              disabled={zoom >= 3}
              data-testid="zoom-in-btn"
            >
              <ZoomIn size={20} />
            </Button>
          </div>

          <div className="text-white text-sm">
            {currentIndex + 1} / {images.length}
          </div>

          {images.length > 1 && (
            <Button
              onClick={() => setIsPlaying(!isPlaying)}
              variant="ghost"
              className="text-white hover:bg-white/20"
              data-testid="play-pause-btn"
            >
              {isPlaying ? <Pause size={20} /> : <Play size={20} />}
            </Button>
          )}
        </div>

        {/* Thumbnails */}
        {images.length > 1 && (
          <div className="flex gap-2 mt-4 overflow-x-auto pb-2 justify-center scrollbar-hide">
            {images.map((img, idx) => (
              <button
                key={img.id}
                onClick={() => {
                  setCurrentIndex(idx);
                  onIndexChange(idx);
                  resetZoom();
                  setLoadingImage(true);
                }}
                className={`flex-shrink-0 w-16 h-16 rounded overflow-hidden border-2 transition-all ${
                  idx === currentIndex ? 'border-white scale-110' : 'border-transparent opacity-60'
                }`}
                data-testid={`thumbnail-${idx}`}
              >
                <img
                  src={img.thumb_url}
                  alt={`Thumbnail ${idx + 1}`}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}